import React, { useCallback, useEffect, useMemo } from "react"
import { Flex } from "antd"
import { useTranslation } from "react-i18next"
import PropTypes from "prop-types"
import { useDispatch, useSelector } from "react-redux"
import { useMediaQuery } from "react-responsive"
import { setExperiencesViewMode } from "store/experiencesUi"
import { getTripCategories } from "store/trips"
import { useExperiencesActions } from "hooks/useExperiencesActions"
import FilterChips from "ui/FilterChips"
import { useExperienceSearch } from "./hooks/useExperienceSearch"
import { usePagination } from "./hooks/usePagination"
import { useExperienceFilters } from "./hooks/useExperienceFilters"
import { useTabCounts } from "./hooks/useTabCounts"
import { useExperienceListingsFetch } from "./hooks/useExperienceListingsFetch"
import { useListingsModals } from "./hooks/useListingsModals"
import { useListingsHandlers } from "./hooks/useListingsHandlers"
import { useListingsDerivedData } from "./hooks/useListingsDerivedData"
import { useBulkExperienceActions } from "./hooks/useBulkExperienceActions"
import SearchHeader from "./components/SearchHeader"
import FilterButtons from "./components/FilterButtons"
import ExperienceGrid from "./components/ExperienceGrid"
import SearchResults from "./components/SearchResults"
import { AddNewListing } from "./modal/AddNewListing"
import FilterDropdown from "./FilterDropdown"
import EditCategoryModal from "./modal/EditCategory"
import ManageCategoriesModal from "./modal/ManageCategories"
import { ListingTable } from "./ListingTable"
import styles from "./index.module.css"

export const ExperienceListings = ({ onSearch }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const isCompactDevice = useMediaQuery({ query: "(max-width: 1280px)" })

  const { items: trips, metadata, loading } = useSelector((state) => state.trips)
  const categoriesLoaded = useSelector((state) => state.trips.tripCategories.data.length > 0)
  const selectedListing = useSelector((state) => state.experiencesUi.viewMode)
  const setSelectedListing = useCallback((mode) => dispatch(setExperiencesViewMode(mode)), [dispatch])

  useEffect(() => {
    if (!categoriesLoaded) dispatch(getTripCategories())
  }, [dispatch, categoriesLoaded])

  const { page, pageSize, handlePageChange, handlePageSizeChange, resetPagination } = usePagination(1, 10)
  const filters = useExperienceFilters("published")
  const search = useExperienceSearch({ pageSize, onSearch })
  const [showPagination, setShowPagination] = React.useState(true)
  const [sortBy, setSortBy] = React.useState(null)

  const { handleDelete, handleDuplicate, handleDeactivate, handleShare, handlePublish, modal, isDuplicateInProgress, duplicatingTripId, processingAction, processingTripId } =
    useExperiencesActions()

  const { categoryOptions, durationOptions, categorizedSearchResults, tripsForMainList } = useListingsDerivedData({ search, trips })

  const { originalStatusCount, refetchCurrent, fetchTripByTitle } = useExperienceListingsFetch({
    activeListingTab: filters.activeListingTab,
    page,
    pageSize,
    metadata,
    searchQuery: search.searchQuery,
    confirmedSearchQuery: search.confirmedSearchQuery,
    debouncedSearchQuery: search.debouncedSearchQuery,
    selectedTrip: search.selectedTrip,
    buildFilterQuery: filters.buildFilterQuery,
    sortBy
  })

  const { publishedCount, draftCount, unpublishedCount } = useTabCounts(
    search.selectedTrip,
    search.confirmedSearchQuery,
    filters.activeListingTab,
    metadata,
    originalStatusCount
  )

  const modals = useListingsModals({ setSelectedType: filters.setSelectedType, onActionComplete: refetchCurrent })

  const handlers = useListingsHandlers({
    search,
    filters,
    resetPagination,
    refetchCurrent,
    fetchTripByTitle,
    setShowPagination
  })

  const bulkActions = useBulkExperienceActions({ onComplete: refetchCurrent })

  // Chips for a deep-linked listing filter (blackout "View"). The list is already narrowed to
  // exactly these ids, so the rows on screen carry their titles — ids beyond the current page
  // stay filtering without a chip rather than showing an id the guide would not recognise.
  const listingFilterOptions = useMemo(
    () => tripsForMainList.map((trip) => ({ value: String(trip.id), label: trip.title })),
    [tripsForMainList]
  )

  // Sync selectedTrip with full data once it lands in searchTrips
  useEffect(() => {
    if (!search.selectedTrip || search.searchLoading || !search.searchTrips?.length) return
    const full = search.searchTrips.find((trip) => trip.id === search.selectedTrip.id)
    if (full && full !== search.selectedTrip) search.setSelectedTrip(full)
  }, [search])

  const renderSearchResults = () => (
    <SearchResults
      showSearchResults={search.showSearchResults}
      searchQuery={search.searchQuery}
      debouncedSearchQuery={search.debouncedSearchQuery}
      searchLoading={search.searchLoading}
      searchTrips={search.searchTrips}
      categorizedSearchResults={categorizedSearchResults}
      onTripClick={handlers.handleTripClickWithAPI}
      selectedTrip={search.selectedTrip}
    />
  )

  const sharedActionProps = {
    onDelete: handleDelete,
    onDeactivate: handleDeactivate,
    onDuplicate: handleDuplicate,
    onShare: handleShare,
    onPublish: handlePublish,
    onEditCategory: modals.openEditCategory,
    onActionComplete: refetchCurrent,
    onAddNewExperience: modals.addFromEmptyState,
    activeListingTab: filters.activeListingTab,
    loading: loading || bulkActions.isBulkLoading,
    metadata,
    showPagination,
    page,
    pageSize,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
    isDuplicateInProgress,
    duplicatingTripId,
    processingAction,
    processingTripId,
    onSortChange: setSortBy,
    onBulkActivate: bulkActions.handleBulkActivate,
    onBulkDeactivate: bulkActions.handleBulkDeactivate,
    onBulkDuplicate: bulkActions.handleBulkDuplicate,
    onBulkDelete: bulkActions.handleBulkDelete
  }

  return (
    <div className="pb-m">
      <SearchHeader
        searchQuery={search.searchQuery}
        onSearchChange={search.handleSearchChange}
        onSearchKeyDown={search.handleSearchKeyDown}
        onClearSearch={handlers.handleClearSearchWithReset}
        onAddNewListing={modals.openAddNewListing}
        onManageCategories={modals.openManageCategories}
        renderSearchResults={renderSearchResults}
      />

      <Flex gap={10} wrap align="center" justify="space-between" className="my-m">
        <FilterButtons
          selectedListing={selectedListing}
          setSelectedListing={setSelectedListing}
          t={t}
          activeTab={filters.activeTab}
          onTabChange={handlers.handleExperienceTypeTabChange}
          activeListingTab={filters.activeListingTab}
          onListingTabChange={handlers.handleTabChangeWithReset}
          publishedCount={publishedCount}
          draftCount={draftCount}
          unpublishedCount={unpublishedCount}
          isFilterDropdownOpen={filters.isFilterDropdownOpen}
          onFilterIconClick={filters.handleFilterIconClick}
          onCloseFilterDropdown={filters.handleCloseFilterDropdown}
          FilterDropdown={FilterDropdown}
          pageSize={pageSize}
          onApplyFilters={handlers.handleApplyFilters}
          onClearFilters={handlers.handleClearFilters}
          selectedCategoryIds={filters.selectedCategoryIds}
          selectedDurationTypes={filters.selectedDurationTypes}
          activeFilterCount={filters.activeFilterCount}
        />
      </Flex>

      <Flex wrap gap={8} className={styles.activeFilterChips}>
        <FilterChips
          selectedValues={filters.selectedCategoryIds}
          options={categoryOptions}
          onChange={(next) => handlers.handleApplyFilters({ categoryIds: next, durationTypes: filters.selectedDurationTypes })}
        />
        <FilterChips
          selectedValues={filters.selectedDurationTypes}
          options={durationOptions}
          onChange={(next) => handlers.handleApplyFilters({ categoryIds: filters.selectedCategoryIds, durationTypes: next })}
        />
        <FilterChips
          selectedValues={filters.selectedListingIds}
          options={listingFilterOptions}
          onChange={(next) =>
            handlers.handleApplyFilters({
              categoryIds: filters.selectedCategoryIds,
              durationTypes: filters.selectedDurationTypes,
              listingIds: next
            })
          }
        />
      </Flex>

      {isCompactDevice || selectedListing === "table" ? (
        <ExperienceGrid {...sharedActionProps} tripsForMainList={tripsForMainList} />
      ) : (
        <ListingTable
          {...sharedActionProps}
          data={tripsForMainList}
          onEdit={modals.openEditCategory}
          onView={() => {}}
          onMoreAction={() => {}}
        />
      )}

      <AddNewListing
        selectedType={filters.selectedType}
        setSelectedType={filters.setSelectedType}
        isOpen={modals.isAddNewListingModalOpen}
        onClose={modals.closeAddNewListing}
        onContinue={modals.continueAddNewListing}
      />
      <EditCategoryModal
        visible={modals.editCategoryModal.visible}
        onClose={modals.closeEditCategory}
        onSave={modals.saveCategory}
        tripId={modals.editCategoryModal.trip?.id}
        loading={modals.isSavingCategory}
      />
      <ManageCategoriesModal
        visible={modals.isManageCategoriesOpen}
        onClose={modals.closeManageCategories}
      />
      {modal}
      {bulkActions.modal}
    </div>
  )
}

ExperienceListings.propTypes = {
  onSearch: PropTypes.func
}

ExperienceListings.defaultProps = {
  onSearch: () => {}
}
